// export2.js - Clean DDR Export Module
// This module ensures that PDF exports render exactly the same as the DDR interface
// Focus: Clean code, consistent rendering, and maintainable structure

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import ReportRenderer from '../components/ReportRenderer';
import { formatDate } from './utils';
import { formatFieldValue } from './formatters';

// ============================================================================
// CORE EXPORT FUNCTIONS
// ============================================================================

/**
 * Export DDR to PDF with exact DDR layout consistency
 */
export const exportDDRToPDF = async ({ 
  well, 
  currentReport, 
  sectionData, 
  layoutConfig, 
  globalSettings = null 
}) => {
  console.log('🚀 Starting Clean DDR PDF Export');
  
  try {
    // Extract global settings
    const settings = globalSettings || layoutConfig?.globalSettings || {};
    
    // Generate the complete HTML for PDF
    const htmlContent = await generateDDRHTML({
      well,
      currentReport,
      sectionData,
      layoutConfig,
      globalSettings: settings
    });
    
    // Convert HTML to PDF
    await convertHTMLToPDF(htmlContent, {
      fileName: `DDR_${well?.WellName || 'Unknown'}_${formatDate(currentReport?.ReportDate)}.pdf`,
      wellName: well?.WellName,
      layoutConfig: layoutConfig
    });
    
    console.log('✅ Clean DDR PDF Export completed successfully');
    
  } catch (error) {
    console.error('❌ Clean DDR PDF Export failed:', error);
    throw error;
  }
};

/**
 * Export DDR to Excel with consistent formatting
 */
export const exportDDRToExcel = async ({ 
  well, 
  currentReport, 
  sectionData, 
  layoutConfig, 
  globalSettings = null 
}) => {
  console.log('📊 Starting Clean DDR Excel Export');
  
  try {
    const settings = globalSettings || layoutConfig?.globalSettings || {};
    
    // Generate Excel workbook
    const workbook = await generateExcelWorkbook({
      well,
      currentReport,
      sectionData,
      layoutConfig,
      globalSettings: settings
    });
    
    // Save Excel file
    const fileName = `DDR_${well?.WellName || 'Unknown'}_${formatDate(currentReport?.ReportDate)}.xlsx`;
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    downloadFile(blob, fileName);
    
    console.log('✅ Clean DDR Excel Export completed successfully');
    
  } catch (error) {
    console.error('❌ Clean DDR Excel Export failed:', error);
    throw error;
  }
};

/**
 * Print DDR with exact layout consistency
 */
export const printDDR = async ({ 
  well, 
  currentReport, 
  sectionData, 
  layoutConfig, 
  globalSettings = null 
}) => {
  console.log('🖨️ Starting Clean DDR Print');
  
  try {
    const settings = globalSettings || layoutConfig?.globalSettings || {};
    
    // Generate print HTML
    const htmlContent = await generateDDRHTML({
      well,
      currentReport,
      sectionData,
      layoutConfig,
      globalSettings: settings,
      isPrint: true
    });
    
    // Open print window
    openPrintWindow(htmlContent);
    
    console.log('✅ Clean DDR Print window opened successfully');
    
  } catch (error) {
    console.error('❌ Clean DDR Print failed:', error);
    throw error;
  }
};

// ============================================================================
// HTML GENERATION
// ============================================================================

/**
 * Generate complete DDR HTML using ReportRenderer for consistency
 */
const generateDDRHTML = async ({ 
  well, 
  currentReport, 
  sectionData, 
  layoutConfig, 
  globalSettings, 
  isPrint = false 
}) => {
  console.log('🔧 Generating DDR HTML with ReportRenderer');
  
  const settings = globalSettings || {};
  const dateToUse = currentReport?.ReportDate || new Date().toISOString().split('T')[0];
  
  // Process header HTML with template variables
  const headerHTML = await processHeaderHTML(layoutConfig, well, currentReport);
  
  // Generate report content using ReportRenderer for exact consistency
  const reportRendererHTML = ReactDOMServer.renderToStaticMarkup(
    React.createElement(ReportRenderer, {
      well: well,
      reportDate: dateToUse,
      reportData: currentReport,
      currentReport: currentReport,
      layoutConfig: layoutConfig,
      sectionData: sectionData,
      isPrint: isPrint,
      fontScale: 1,
      // Read-only mode for export/print
      editingSections: {},
      editingData: {},
      collapsedSections: {},
      role: 'read-only',
      // Dummy functions for export context
      toggleSection: () => {},
      toggleEditSection: () => {},
      addTableRow: () => {},
      removeTableRow: () => {},
      sectionRefs: { current: {} },
      handleAddDataToEmptySection: () => {},
      copiedSections: new Set(),
      setCopiedSections: () => {},
      isCopyingData: false,
      setIsCopyingData: () => {},
      setToast: () => {},
      formatDate: formatDate,
      apiFetch: () => Promise.resolve([]),
      setEditingData: () => {},
      setEditingSections: () => {},
      handleFieldChange: () => {},
      handleTableFieldChange: () => {},
      saveSectionData: () => Promise.resolve(),
      renderFieldInput: null,
      renderTableCellInput: null,
      globalSettings: settings,
      showDataForm: false,
      setShowDataForm: () => {},
      dataFormConfig: null,
      setDataFormConfig: () => {},
      handleDataFormSave: () => {},
      handleDataFormCancel: () => {},
      getSectionDataProp: (sectionConfig) => {
        if (!sectionConfig.tableName) return {};
        const data = sectionData[sectionConfig.id] || {};
        if (sectionConfig.presentationType === 'label-value') {
          return Array.isArray(data) && data.length > 0 ? data[0] : {};
        }
        return Array.isArray(data) ? data : [];
      }
    })
  );
  
  // Generate complete HTML document
  return generateCompleteHTML({
    headerHTML,
    reportRendererHTML,
    layoutConfig,
    wellName: well?.WellName,
    isPrint
  });
};

/**
 * Process header HTML with template variables and logos
 */
const processHeaderHTML = async (layoutConfig, well, currentReport) => {
  if (!layoutConfig?.headerHTML) {
    return getDefaultHeaderHTML();
  }

  let headerHTML = layoutConfig.headerHTML;
  const dateToUse = currentReport?.ReportDate || new Date().toISOString().split('T')[0];
  
  // Replace template variables
  headerHTML = headerHTML
    .replace(/\{\{wellName\}\}/g, well?.WellName || '-')
    .replace(/\{\{reportDate\}\}/g, formatDate(dateToUse))
    .replace(/\{\{operator\}\}/g, well?.Operator || '-')
    .replace(/\{\{field\}\}/g, well?.Field || '-')
    .replace(/\{\{wellType\}\}/g, well?.WellType || '-');

  // Process logos for export/print
  headerHTML = await processLogos(headerHTML);
  
  return headerHTML;
};

/**
 * Process logo placeholders and convert to base64 for export
 */
const processLogos = async (htmlContent) => {
  let processedHTML = htmlContent;
  
  // Handle default logo {{logo}}
  if (processedHTML.includes('{{logo}}')) {
    try {
      const response = await fetch('/api/logo/default');
      const logoData = await response.json();
      if (logoData.defaultLogoUrl) {
        const logoBase64 = await loadImageAsBase64(logoData.defaultLogoUrl);
        if (logoBase64) {
          processedHTML = processedHTML.replace(
            /\{\{logo\}\}/g, 
            `<img src="${logoBase64}" alt="Company Logo" style="height: 50px; width: auto; max-width: 150px;" />`
          );
        }
      }
    } catch (error) {
      console.warn('Failed to load default logo:', error);
      processedHTML = processedHTML.replace(/\{\{logo\}\}/g, '');
    }
  }
  
  // Handle specific logo {{logo:filename}}
  const logoMatches = processedHTML.match(/\{\{logo:([^}]+)\}\}/g);
  if (logoMatches) {
    for (const match of logoMatches) {
      const filename = match.match(/\{\{logo:([^}]+)\}\}/)[1];
      try {
        const logoUrl = `/uploads/logos/${filename}`;
        const logoBase64 = await loadImageAsBase64(logoUrl);
        if (logoBase64) {
          processedHTML = processedHTML.replace(
            match,
            `<img src="${logoBase64}" alt="Company Logo" style="height: 50px; width: auto; max-width: 150px;" />`
          );
        }
      } catch (error) {
        console.warn(`Failed to load logo ${filename}:`, error);
        processedHTML = processedHTML.replace(match, '');
      }
    }
  }
  
  return processedHTML;
};

/**
 * Generate complete HTML document with proper CSS
 */
const generateCompleteHTML = ({ 
  headerHTML, 
  reportRendererHTML, 
  layoutConfig, 
  wellName, 
  isPrint = false 
}) => {
  const pageTitle = `Daily Drilling Report - ${wellName || 'Unknown Well'}`;
  const cssStyles = generateCSS(layoutConfig, isPrint);
  
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${pageTitle}</title>
      <style>${cssStyles}</style>
    </head>
    <body>
      <div class="ddr-export-page">
        <!-- Header -->
        <div class="ddr-header">
          ${headerHTML}
        </div>
        
        <!-- Report Content -->
        <div class="ddr-content">
          ${reportRendererHTML}
        </div>
        
        <!-- Footer -->
        <div class="ddr-footer">
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

// ============================================================================
// CSS GENERATION
// ============================================================================

/**
 * Generate CSS that matches DDR interface exactly
 */
const generateCSS = (layoutConfig, isPrint = false) => {
  const globalStyles = layoutConfig?.globalStyles || {};
  
  // Log spacing configuration for debugging
  console.log('🎨 Applying spacing configuration:', {
    sectionSpacing: globalStyles.sectionSpacing || '20px (default)',
    groupSpacing: globalStyles.groupSpacing || '16px (default)',
    sectionPadding: globalStyles.sectionPadding || '16px (default)',
    groupPadding: globalStyles.groupPadding || '12px (default)',
    isPrint: isPrint
  });
  
  // Base styles that match DDR interface
  const baseCSS = `
    /* Page Setup */
    ${isPrint ? '@page { size: A4; margin: 10mm 5mm; }' : ''}
    
    * {
      box-sizing: border-box;
    }
    
    body {
      margin: 0;
      padding: 0;
      font-family: ${globalStyles.fontFamily || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'};
      font-size: ${isPrint ? '8pt' : '14px'};
      line-height: 1.4;
      color: ${globalStyles.textColor || '#1e293b'};
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .ddr-export-page {
      width: 100%;
      max-width: ${isPrint ? 'none' : '1200px'};
      margin: 0 auto;
      padding: ${isPrint ? '0' : '20px'};
      background: white;
    }
    
    .ddr-header {
      width: 100%;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 2px solid ${globalStyles.borderColor || '#e5e7eb'};
    }
    
    .ddr-content {
      width: 100%;
      margin-bottom: 20px;
    }
    
    .ddr-footer {
      width: 100%;
      margin-top: 20px;
      padding-top: 10px;
      border-top: 1px solid ${globalStyles.borderColor || '#e5e7eb'};
      text-align: center;
      font-size: ${isPrint ? '7pt' : '12px'};
      color: #666;
    }
  `;
  
  // ReportRenderer component styles - exact match to DDR interface
  const reportRendererCSS = `
    /* Section Styles - Match DDR Interface with Full Width */
    .collapsible-section {
      width: 100% !important;
      margin-bottom: ${globalStyles.sectionSpacing || '20px'};
      border: 1px solid ${globalStyles.borderColor || '#e5e7eb'};
      border-radius: ${globalStyles.borderRadius || '8px'};
      background: white;
      overflow: visible;
      ${isPrint ? 'page-break-inside: avoid; break-inside: avoid;' : ''}
      position: relative;
      min-height: 50px;
      display: block;
      box-sizing: border-box;
    }
    
    .section-header {
      width: 100% !important;
      background: ${globalStyles.sectionHeaderBackgroundColor || '#f8fafc'};
      padding: ${globalStyles.sectionPadding || '12px 16px'};
      border-bottom: 1px solid ${globalStyles.borderColor || '#e5e7eb'};
      font-weight: ${globalStyles.sectionHeaderFontWeight || '600'};
      font-size: ${globalStyles.sectionHeaderFontSize || (isPrint ? '10pt' : '16px')};
      color: ${globalStyles.sectionHeaderTextColor || '#1e293b'};
      display: block;
      box-sizing: border-box;
    }
    
    .section-content {
      width: 100% !important;
      padding: ${globalStyles.sectionPadding || '16px'};
      background: ${globalStyles.backgroundColor || 'white'};
      overflow: visible;
      min-height: 30px;
      ${isPrint ? 'break-inside: auto;' : ''}
      display: block;
      box-sizing: border-box;
    }
    
    /* Hide editing controls in export */
    .section-actions,
    .editing-buttons,
    .table-editing-controls,
    .add-table-row-btn,
    .remove-table-row-btn,
    .save-section-btn,
    .cancel-section-btn,
    .edit-full-data-btn,
    .add-data-btn-content,
    .copy-data-btn-content {
      display: none !important;
    }
    
    /* Table Styles - Match DDR Interface with Full Width */
    .table-container {
      width: 100% !important;
      overflow: visible;
      margin-bottom: ${globalStyles.groupSpacing || '16px'};
      ${isPrint ? 'break-inside: auto;' : ''}
      display: block;
      box-sizing: border-box;
    }
    
    .activity-table,
    .data-table {
      width: 100% !important;
      border-collapse: collapse;
      font-size: ${isPrint ? '7pt' : '14px'};
      margin-bottom: ${globalStyles.groupSpacing || '16px'};
      ${isPrint ? 'break-inside: auto; page-break-inside: auto;' : ''}
      table-layout: auto;
      box-sizing: border-box;
    }
    
    .activity-table th,
    .activity-table td,
    .data-table th,
    .data-table td {
      border: 1px solid ${globalStyles.borderColor || '#d1d5db'};
      padding: ${isPrint ? '4px 6px' : '8px 12px'};
      text-align: left;
      vertical-align: top;
      word-wrap: break-word;
      box-sizing: border-box;
    }
    
    .activity-table th,
    .data-table th {
      background-color: ${globalStyles.sectionHeaderBackgroundColor || '#f3f4f6'};
      font-weight: bold;
      color: ${globalStyles.sectionHeaderTextColor || '#374151'};
    }
    
    /* Form Styles - Match DDR Interface with Full Width */
    .section-groups-container {
      width: 100% !important;
      display: block;
      box-sizing: border-box;
    }
    
    .field-group {
      width: 100% !important;
      margin-bottom: ${globalStyles.groupSpacing || '16px'};
      padding: ${globalStyles.groupPadding || '12px'};
      border: 1px solid ${globalStyles.borderColor || 'transparent'};
      border-radius: ${globalStyles.borderRadius || '4px'};
      background: ${globalStyles.backgroundColor || 'white'};
      display: block;
      box-sizing: border-box;
    }
    
    .field-group-content {
      width: 100% !important;
      display: grid;
      gap: ${isPrint ? Math.max(parseFloat(globalStyles.groupSpacing || '16') / 2, 8) + 'px' : globalStyles.groupSpacing || '12px'};
      box-sizing: border-box;
    }
    
    .field-group-title {
      font-weight: bold;
      font-size: ${isPrint ? '9pt' : '14px'};
      margin-bottom: 12px;
      color: ${globalStyles.sectionHeaderTextColor || '#374151'};
      border-bottom: 1px solid ${globalStyles.borderColor || '#e5e7eb'};
      padding-bottom: 6px;
    }
    
    .label-value-item {
      display: flex;
      flex-direction: column;
      margin-bottom: ${isPrint ? Math.max(parseFloat(globalStyles.groupSpacing || '16') / 3, 6) + 'px' : Math.max(parseFloat(globalStyles.groupSpacing || '16') / 2, 8) + 'px'};
    }
    
    .label-value-item .label {
      font-weight: 500;
      font-size: ${isPrint ? '7pt' : '12px'};
      color: ${globalStyles.sectionHeaderTextColor || '#374151'};
      margin-bottom: 4px;
    }
    
    .label-value-item .value {
      font-size: ${isPrint ? '8pt' : '14px'};
      color: ${globalStyles.textColor || '#1f2937'};
      word-wrap: break-word;
      min-height: ${isPrint ? '14px' : '20px'};
    }
    
    /* Excel-style field grid with Full Width */
    .field-grid {
      width: 100% !important;
      display: grid;
      gap: 0;
      border: 1px solid ${globalStyles.borderColor || '#e08708'};
      box-sizing: border-box;
    }
    
    .field-row {
      width: 100% !important;
      display: grid;
      grid-template-columns: 1fr 2fr;
      box-sizing: border-box;
    }
    
    .field-row .label,
    .field-row .value {
      padding: ${isPrint ? '4px 6px' : '8px 12px'};
      border: 1px solid ${globalStyles.borderColor || '#d1d5db'};
      font-size: ${isPrint ? '8pt' : '14px'};
      box-sizing: border-box;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    .field-row .label {
      font-weight: bold;
      background-color: ${globalStyles.sectionHeaderBackgroundColor || '#f3f4f6'};
      color: ${globalStyles.sectionHeaderTextColor || '#374151'};
    }
    
    .field-row .value {
      color: ${globalStyles.textColor || '#1f2937'};
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    /* Full width fields */
    .field-width-full {
      grid-column: 1 / -1 !important;
      width: 100% !important;
    }
    
    /* Empty section styling */
    .empty-section-content {
      text-align: center;
      color: #6b7280;
      font-style: italic;
      padding: 40px 20px;
    }
    
    .empty-section-message {
      margin-bottom: 15px;
    }
    
    .empty-section-actions {
      display: none; /* Hide in export */
    }
  `;
  
  // Custom CSS from layout configuration
  const customCSS = generateCustomCSS(layoutConfig);
  
  // Print-specific optimizations
  const printCSS = isPrint ? `
    @media print {
      body {
        overflow: visible !important;
      }
      
      .ddr-export-page {
        overflow: visible !important;
      }
      
      .collapsible-section {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
        margin-bottom: ${globalStyles.sectionSpacing || '20px'} !important;
        position: relative !important;
        overflow: visible !important;
      }
      
      .section-header {
        break-after: avoid !important;
        page-break-after: avoid !important;
      }
      
      .section-content {
        break-inside: auto !important;
        overflow: visible !important;
      }
      
      .activity-table,
      .data-table {
        break-inside: auto !important;
        margin-bottom: 10px !important;
      }
      
      .activity-table tr,
      .data-table tr {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
        break-after: auto !important;
      }
      
              .field-group {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          margin-bottom: ${globalStyles.groupSpacing || '16px'} !important;
        }
      
      .field-grid {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }
      
      .label-value-item {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }
    }
  ` : '';
  
  // Additional CSS for better PDF rendering
  const pdfOptimizationCSS = `
    /* PDF-specific optimizations */
    * {
      box-sizing: border-box !important;
      overflow: visible !important;
    }
    
    .ddr-export-page {
      overflow: visible !important;
      height: auto !important;
    }
    
    .ddr-content {
      overflow: visible !important;
      height: auto !important;
    }
    
    .collapsible-section {
      position: relative !important;
      overflow: visible !important;
      height: auto !important;
      min-height: 50px !important;
      margin-bottom: ${globalStyles.sectionSpacing || '20px'} !important;
      clear: both !important;
    }
    
    .section-content {
      overflow: visible !important;
      height: auto !important;
      min-height: 30px !important;
    }
    
    .table-container {
      overflow: visible !important;
      height: auto !important;
    }
    
    .activity-table,
    .data-table {
      table-layout: fixed !important;
      width: 100% !important;
      overflow: visible !important;
    }
    
    .field-group {
      overflow: visible !important;
      height: auto !important;
      margin-bottom: ${globalStyles.groupSpacing || '16px'} !important;
    }
    
    .field-grid {
      overflow: visible !important;
      height: auto !important;
    }
  `;
  
  return baseCSS + reportRendererCSS + customCSS + printCSS + pdfOptimizationCSS;
};

/**
 * Generate custom CSS from layout configuration
 */
const generateCustomCSS = (layoutConfig) => {
  if (!layoutConfig) return '';
  
  let customCSS = '';
  
  // Global custom CSS
  if (layoutConfig.globalStyles?.customCSS) {
    customCSS += `\n/* Global Custom CSS */\n${layoutConfig.globalStyles.customCSS}\n`;
  }
  
  // Section-specific custom CSS
  if (layoutConfig.sections) {
    layoutConfig.sections.forEach(section => {
      if (section.enabled) {
        // Table custom CSS
        if (section.customTableCSS) {
          const tableClassName = `table-section-${section.id}`;
          customCSS += `\n/* Custom CSS for ${section.name} table */\n`;
          customCSS += section.customTableCSS
            .replace(/\btable\b/g, `.${tableClassName} table`)
            .replace(/\bth\b/g, `.${tableClassName} th`)
            .replace(/\btr\b/g, `.${tableClassName} tr`)
            .replace(/\btd\b/g, `.${tableClassName} td`) + '\n';
        }
        
        // Group custom CSS
        if (section.groupSettings) {
          Object.entries(section.groupSettings).forEach(([groupId, settings]) => {
            if (settings.customCSS) {
              const groupClassName = `field-group-${section.id}-${groupId}`;
              customCSS += `\n/* Custom CSS for ${section.name} - Group ${groupId} */\n`;
              customCSS += settings.customCSS
                .replace(/\.field-group/g, `.${groupClassName}`) + '\n';
            }
          });
        }
      }
    });
  }
  
  return customCSS;
};

// ============================================================================
// EXCEL GENERATION
// ============================================================================

/**
 * Generate Excel workbook with DDR data
 */
const generateExcelWorkbook = async ({ 
  well, 
  currentReport, 
  sectionData, 
  layoutConfig, 
  globalSettings 
}) => {
  const workbook = XLSX.utils.book_new();
  
  // Main report sheet
  const reportRows = [];
  
  // Add header information
  reportRows.push(['Daily Drilling Report (DDR)', '']);
  reportRows.push([well?.WellName || 'Unknown Well', formatDate(currentReport?.ReportDate)]);
  reportRows.push(['', '']); // Spacing
  
  // Get ordered sections
  const orderedSections = layoutConfig?.sections
    ?.filter(section => section.enabled)
    ?.sort((a, b) => a.order - b.order) || [];
  
  // Add each section to the main sheet
  orderedSections.forEach(sectionConfig => {
    const data = sectionData[sectionConfig.id] || {};
    
    reportRows.push([sectionConfig.name, '']);
    
    if (sectionConfig.presentationType === 'table') {
      const tableData = Array.isArray(data) ? data : [];
      const enabledFields = sectionConfig.fields.filter(field => field.enabled);
      
      if (tableData.length > 0 && enabledFields.length > 0) {
        // Add headers
        const headers = enabledFields.map(field => field.name);
        reportRows.push(headers);
        
        // Add data rows
        tableData.forEach(row => {
          const rowData = enabledFields.map(field => 
            formatFieldValue(row[field.dbColumn], field, globalSettings)
          );
          reportRows.push(rowData);
        });
      } else {
        reportRows.push(['No data available', '']);
      }
    } else {
      // Label-value presentation
      const labelValueData = Array.isArray(data) && data.length > 0 ? data[0] : data;
      const enabledFields = sectionConfig.fields.filter(field => field.enabled);
      
      if (enabledFields.length > 0) {
        enabledFields.forEach(field => {
          const formattedValue = formatFieldValue(labelValueData[field.dbColumn], field, globalSettings);
          const cleanValue = typeof formattedValue === 'string' ? 
            formattedValue.replace(/<[^>]*>/g, '') : formattedValue;
          reportRows.push([field.name, cleanValue]);
        });
      }
    }
    
    reportRows.push(['', '']); // Spacing between sections
  });
  
  const wsMain = XLSX.utils.aoa_to_sheet(reportRows);
  XLSX.utils.book_append_sheet(workbook, wsMain, 'Daily Report');
  
  // Create separate sheets for table sections
  orderedSections.forEach(sectionConfig => {
    if (sectionConfig.presentationType === 'table') {
      const data = sectionData[sectionConfig.id] || {};
      const tableData = Array.isArray(data) ? data : [];
      const enabledFields = sectionConfig.fields.filter(field => field.enabled);
      
      if (tableData.length > 0 && enabledFields.length > 0) {
        const sheetData = [
          enabledFields.map(field => field.name),
          ...tableData.map(row => 
            enabledFields.map(field => 
              formatFieldValue(row[field.dbColumn], field, globalSettings)
            )
          )
        ];
        
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        
        // Auto-size columns
        const range = XLSX.utils.decode_range(ws['!ref']);
        const cols = [];
        for (let i = range.s.c; i <= range.e.c; i++) {
          let maxLength = 0;
          for (let j = range.s.r; j <= range.e.r; j++) {
            const cell = ws[XLSX.utils.encode_cell({ r: j, c: i })];
            if (cell && cell.v) {
              maxLength = Math.max(maxLength, String(cell.v).length);
            }
          }
          cols[i] = { wch: Math.min(maxLength + 2, 50) };
        }
        ws['!cols'] = cols;
        
        // Sanitize sheet name
        const sheetName = sectionConfig.name.replace(/[/\\?*[\]]/g, '').substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, ws, sheetName);
      }
    }
  });
  
  return workbook;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert HTML to PDF using html2canvas and jsPDF with improved page break handling
 */
const convertHTMLToPDF = async (htmlContent, options) => {
  // Create temporary container with better sizing
  const tempContainer = document.createElement('div');
  tempContainer.style.position = 'absolute';
  tempContainer.style.left = '-9999px';
  tempContainer.style.top = '-9999px';
  tempContainer.style.width = '794px'; // A4 width in pixels at 96 DPI
  tempContainer.style.height = 'auto';
  tempContainer.style.overflow = 'visible';
  tempContainer.innerHTML = htmlContent;
  
  document.body.appendChild(tempContainer);
  
  try {
    // Wait for images, fonts, and layout to stabilize
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Get the export page element
    const exportPage = tempContainer.querySelector('.ddr-export-page');
    if (!exportPage) {
      throw new Error('Could not find DDR export page element');
    }
    
    // Force layout recalculation
    void exportPage.offsetHeight;
    
    // Check if we need to split content for better page breaks
    const shouldSplitContent = await shouldSplitIntoPages(exportPage);
    
    if (shouldSplitContent) {
      // Use section-by-section rendering for better page breaks
      await renderSectionBySection(exportPage, options);
    } else {
      // Use single canvas approach for shorter content
      await renderSingleCanvas(exportPage, options);
    }
    
  } finally {
    // Clean up
    document.body.removeChild(tempContainer);
  }
};

/**
 * Check if content should be split into multiple pages based on height
 */
const shouldSplitIntoPages = async (exportPage) => {
  const contentHeight = exportPage.scrollHeight;
  const pageHeightInPixels = 1123; // A4 height in pixels at 96 DPI
  const shouldSplit = contentHeight > pageHeightInPixels * 1.2; // Split if content is more than 1.2 pages
  
  console.log(`📏 Content analysis:`, {
    contentHeight,
    pageHeightInPixels,
    ratio: (contentHeight / pageHeightInPixels).toFixed(2),
    shouldSplit,
    renderingMethod: shouldSplit ? 'Section-by-section' : 'Single canvas'
  });
  
  return shouldSplit;
};

/**
 * Render content section by section for better page breaks
 */
const renderSectionBySection = async (exportPage, options) => {
  const pdf = new jsPDF('portrait', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  
  // Get header and sections
  const header = exportPage.querySelector('.ddr-header');
  const sections = exportPage.querySelectorAll('.collapsible-section');
  const footer = exportPage.querySelector('.ddr-footer');
  
  let currentPageHeight = 0;
  let isFirstPage = true;
  const maxPageHeight = pdfHeight - 20; // Leave some margin
  
  // Get spacing values from global styles (convert to mm for PDF)
  const globalStyles = options.layoutConfig?.globalStyles || {};
  const sectionSpacingPx = parseFloat(globalStyles.sectionSpacing || '20');
  const sectionSpacingMm = sectionSpacingPx * 0.352778; // Convert px to mm (1px = 0.352778mm at 72dpi)
  
  // Render header on first page
  if (header) {
    const headerCanvas = await html2canvas(header, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794
    });
    
    const headerImgData = headerCanvas.toDataURL('image/png');
    const headerHeight = (headerCanvas.height * pdfWidth) / headerCanvas.width;
    
    pdf.addImage(headerImgData, 'PNG', 0, currentPageHeight, pdfWidth, headerHeight);
    currentPageHeight += headerHeight + Math.max(sectionSpacingMm, 5); // Use configured spacing or minimum 5mm
  }
  
  // Render sections one by one
  console.log(`📄 Rendering ${sections.length} sections with section-by-section method`);
  
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const sectionTitle = section.querySelector('.section-header')?.textContent?.trim() || `Section ${i + 1}`;
    
    // Create canvas for this section
    const sectionCanvas = await html2canvas(section, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794
    });
    
    const sectionImgData = sectionCanvas.toDataURL('image/png');
    const sectionHeight = (sectionCanvas.height * pdfWidth) / sectionCanvas.width;
    
    // Check if section fits on current page (with configured spacing buffer)
    const spacingBuffer = Math.max(sectionSpacingMm, 10);
    const needsNewPage = currentPageHeight + sectionHeight + spacingBuffer > maxPageHeight && !isFirstPage;
    
    if (needsNewPage) {
      // Add new page
      pdf.addPage();
      currentPageHeight = Math.max(sectionSpacingMm, 15); // Start with configured spacing or minimum margin
      console.log(`📄 New page added for section: ${sectionTitle}`);
    }
    
    // Add section to PDF
    pdf.addImage(sectionImgData, 'PNG', 0, currentPageHeight, pdfWidth, sectionHeight);
    
    console.log(`✅ Section "${sectionTitle}" rendered:`, {
      sectionIndex: i + 1,
      sectionHeight: sectionHeight.toFixed(2),
      currentPageHeight: currentPageHeight.toFixed(2),
      newPageAdded: needsNewPage
    });
    
    currentPageHeight += sectionHeight + sectionSpacingMm; // Add configured spacing between sections
    isFirstPage = false;
  }
  
  // Add footer on last page
  if (footer && currentPageHeight + 15 < maxPageHeight) {
    const footerCanvas = await html2canvas(footer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794
    });
    
    const footerImgData = footerCanvas.toDataURL('image/png');
    const footerHeight = (footerCanvas.height * pdfWidth) / footerCanvas.width;
    
    pdf.addImage(footerImgData, 'PNG', 0, currentPageHeight + 5, pdfWidth, footerHeight);
  }
  
  // Save the PDF
  pdf.save(options.fileName);
};

/**
 * Render as single canvas for shorter content
 */
const renderSingleCanvas = async (exportPage, options) => {
  // Convert to canvas
  const canvas = await html2canvas(exportPage, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: 794,
    windowWidth: 794,
    scrollX: 0,
    scrollY: 0
  });
  
  // Create PDF
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF('portrait', 'mm', 'a4');
  
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pdfWidth;
  const imgHeight = (canvas.height * pdfWidth) / canvas.width;
  
  // Handle multiple pages if needed
  if (imgHeight > pdfHeight) {
    let position = 0;
    const pageHeight = pdfHeight - 10; // Leave margin
    
    while (position < imgHeight) {
      if (position > 0) {
        pdf.addPage();
      }
      pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight);
      position += pageHeight;
    }
  } else {
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
  }
  
  // Save PDF
  pdf.save(options.fileName);
};

/**
 * Open print window with HTML content
 */
const openPrintWindow = (htmlContent) => {
  const printWindow = window.open('', '_blank', 'width=794,height=1123,scrollbars=yes,resizable=yes');
  
  if (!printWindow) {
    throw new Error('Failed to open print window. Please check your popup blocker settings.');
  }
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };
};

/**
 * Download file blob
 */
const downloadFile = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Load image as base64 for embedding in PDF
 */
const loadImageAsBase64 = async (imageUrl) => {
  try {
    const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${window.location.origin}${imageUrl}`;
    const response = await fetch(fullUrl);
    if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
    
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Failed to load image:', error);
    return null;
  }
};

/**
 * Get default header HTML if none configured
 */
const getDefaultHeaderHTML = () => {
  return `
    <div class="report-main-header" style="display: flex; align-items: center; justify-content: space-between; padding: 20px; border-bottom: 2px solid #3b82f6;">
      <div class="header-left" style="text-align: center; margin: 0 20px;">
        {{logo}} <h1 class="header-operator">{{operator}}</h1>
      </div>
      <div class="header-center" style="flex: 1;">
        <h1 class="report-main-title" style="margin: 0; color: #1e40af;">Daily Drilling Report (DDR)</h1>
        <p class="report-subtitle" style="margin: 8px 0 0 0; color: #6b7280;">{{field}} : {{wellName}}</p>
      </div>
      <div class="header-right" style="text-align: right;">
       <strong class="header-date" style="color: #1e40af;">Date : {{reportDate}}</strong>
      </div>
    </div>
  `;
};

// ============================================================================
// EXPORTS
// ============================================================================

const exportUtils = {
  exportDDRToPDF,
  exportDDRToExcel,
  printDDR
};

export default exportUtils;
